import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Star } from 'lucide-react';
import { toast } from 'sonner';

/**
 * StarRating — 5-star rating widget for ANY content type.
 *
 * Props:
 *  - contentType: 'box_office' | 'film' | 'best_films' | 'video_toli' | ...
 *  - contentId: string
 *  - size: 'sm' (default) | 'md'
 *  - onRated?: (summary) => void
 *  - lang?: string (for the tooltip labels)
 */
const LABELS = {
  fr: { rateLabel: 'Ma note', avg: 'Note', tooltip: ['Nul', 'Bof', 'Moyen', 'Bien', 'Excellent'], loginNeeded: 'Connexion requise', saved: 'Note enregistrée', error: 'Erreur' },
  en: { rateLabel: 'My rating', avg: 'Rating', tooltip: ['Bad', 'Meh', 'OK', 'Good', 'Great'], loginNeeded: 'Please log in', saved: 'Rating saved', error: 'Error' },
  es: { rateLabel: 'Mi nota', avg: 'Nota', tooltip: ['Malo', 'Regular', 'Normal', 'Bueno', 'Excelente'], loginNeeded: 'Inicie sesión', saved: 'Nota guardada', error: 'Error' },
  de: { rateLabel: 'Meine Note', avg: 'Note', tooltip: ['Schlecht', 'Naja', 'Ok', 'Gut', 'Super'], loginNeeded: 'Bitte anmelden', saved: 'Bewertung gespeichert', error: 'Fehler' },
  it: { rateLabel: 'Il mio voto', avg: 'Voto', tooltip: ['Male', 'Boh', 'Medio', 'Buono', 'Ottimo'], loginNeeded: 'Accedi', saved: 'Voto salvato', error: 'Errore' },
  pt: { rateLabel: 'Minha nota', avg: 'Nota', tooltip: ['Ruim', 'Mais ou menos', 'Ok', 'Bom', 'Ótimo'], loginNeeded: 'Faça login', saved: 'Nota salva', error: 'Erro' },
  nl: { rateLabel: 'Mijn beoordeling', avg: 'Beoordeling', tooltip: ['Slecht', 'Matig', 'Ok', 'Goed', 'Top'], loginNeeded: 'Log in', saved: 'Opgeslagen', error: 'Fout' },
  ar: { rateLabel: 'تقييمي', avg: 'التقييم', tooltip: ['سيء', 'متوسط', 'حسن', 'جيد', 'ممتاز'], loginNeeded: 'يرجى تسجيل الدخول', saved: 'تم الحفظ', error: 'خطأ' },
  ru: { rateLabel: 'Моя оценка', avg: 'Оценка', tooltip: ['Плохо', 'Так себе', 'Норм', 'Хорошо', 'Отлично'], loginNeeded: 'Войдите', saved: 'Сохранено', error: 'Ошибка' },
  zh: { rateLabel: '我的评分', avg: '评分', tooltip: ['差', '一般', '中等', '好', '很好'], loginNeeded: '请登录', saved: '已保存', error: '错误' },
  ja: { rateLabel: '自分の評価', avg: '評価', tooltip: ['低', '微妙', '普通', '良い', '最高'], loginNeeded: 'ログインしてください', saved: '保存しました', error: 'エラー' },
  hi: { rateLabel: 'मेरी रेटिंग', avg: 'रेटिंग', tooltip: ['ख़राब', 'ठीक-ठाक', 'ठीक', 'अच्छा', 'बेहतरीन'], loginNeeded: 'लॉगिन करें', saved: 'सहेजा गया', error: 'त्रुटि' },
  sw: { rateLabel: 'Ukadiriaji wangu', avg: 'Ukadiriaji', tooltip: ['Mbaya', 'Wastani', 'Sawa', 'Vizuri', 'Bora'], loginNeeded: 'Ingia', saved: 'Imehifadhiwa', error: 'Hitilafu' },
};

export default function StarRating({ contentType, contentId, size = 'sm', onRated, lang = 'fr' }) {
  const L = { ...LABELS.fr, ...(LABELS[(lang || 'fr').toLowerCase()] || {}) };
  const [summary, setSummary] = useState({ average: 0, count: 0, mine: null });
  const [hover, setHover] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!contentType || !contentId) return;
    let cancel = false;
    axios.get(`/api/reactions/rating/${contentType}/${contentId}`).then(r => {
      if (!cancel) setSummary(r.data);
    }).catch(() => {});
    return () => { cancel = true; };
  }, [contentType, contentId]);

  const rate = async (val) => {
    if (saving) return;
    setSaving(true);
    try {
      const r = await axios.post('/api/reactions/rate', { content_type: contentType, content_id: contentId, rating: val });
      setSummary(r.data);
      onRated?.(r.data);
      toast.success(L.saved);
    } catch (e) {
      if (e?.response?.status === 401) toast.error(L.loginNeeded);
      else toast.error(L.error);
    } finally { setSaving(false); }
  };

  const px = size === 'md' ? 'w-5 h-5' : 'w-3.5 h-3.5';
  const mine = summary.mine?.rating || 0;
  const show = hover || mine || 0;

  return (
    <div className={`flex items-center gap-1.5 ${size === 'md' ? 'text-xs' : 'text-[10px]'}`} data-testid={`star-rating-${contentType}-${contentId}`}>
      <div className="flex items-center" onMouseLeave={() => setHover(0)}>
        {[1, 2, 3, 4, 5].map(v => (
          <button
            key={v}
            onClick={() => rate(v)}
            onMouseEnter={() => setHover(v)}
            title={L.tooltip[v - 1]}
            className="p-0.5 hover:scale-125 transition-transform disabled:opacity-40"
            disabled={saving}
            data-testid={`star-${contentType}-${contentId}-${v}`}
          >
            <Star className={`${px} ${v <= show ? 'fill-yellow-400 text-yellow-400' : 'text-white/30'}`} />
          </button>
        ))}
      </div>
      <span className="text-yellow-300 font-semibold">
        {summary.average > 0 ? summary.average.toFixed(1) : '—'}
      </span>
      <span className="text-white/50">({summary.count})</span>
    </div>
  );
}
